import { useEffect, useRef, useState } from "react";

/** いま欄に打っている最中か（input・textarea・select・contenteditable に居る） */
export function isTyping(): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (!a) return false;
  return a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.tagName === "SELECT" || a.isContentEditable;
}
/** 窓・シート・引き出しが開いている（その上の貼り付けは拾わない） */
const modalOpen = (): boolean => !!document.querySelector('.dim, .gsheet, .vdrawer.open, .vdrawer.is-open, [role="dialog"][aria-modal="true"]');
const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/**
 * 画面全体の重ねと貼り付け（受け取った書類、../eigyouryoku/docs/13 決めごと14・§4.2）。案件の画面だけが使う。
 *   ・ファイルをドラッグして画面に入ると true（呼ぶ側が .dropover「受け取った書類に追加」を出す）。dragenter の数を数えて、出たら false。
 *     ファイル以外のドラッグ（文字・行の並べ替え）では出さない（dataTransfer.types に Files があるときだけ）。欄に打っている最中も出さない
 *   ・どこに落としても onFiles。点線の領域（FileDrop）に落としたものはそちらが受けて止める（二度渡さない）。
 *     ファイルを落としてもブラウザがそのファイルを開いて画面を離れないよう、ファイルの dragover・drop はいつも止める
 *   ・⌘V／Ctrl+V：入力の欄に居ないときだけ。ファイル（画像）は onPasteFiles、文字は onPasteText（短い文の判断は呼ぶ側）
 *   ・スマホ版（html[data-view="phone"]）は付けない（落とせない）
 *   ・.app・.main に transform などは付けない（CLAUDE.md の描き直しの決まり）。重ねは position:fixed のまま
 */
export function useDropOverlay(opts: {
  enabled: boolean;
  onFiles: (files: File[]) => void;
  onPasteFiles: (files: File[]) => void;
  onPasteText: (text: string) => void;
}): boolean {
  const [over, setOver] = useState(false);
  const cb = useRef(opts);
  cb.current = opts;
  useEffect(() => {
    if (!opts.enabled || document.documentElement.dataset.view === "phone") return;
    let depth = 0;
    const hide = () => {
      depth = 0;
      setOver(false);
    };
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      if (!isTyping()) setOver(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setOver(false);
    };
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = isTyping() ? "none" : "copy";
    };
    // 落とした：重ねは先（capture）で消す。受け取るのは点線の領域が受けなかったときだけ（bubble で window まで来たとき）
    const onDropCapture = (e: DragEvent) => {
      if (hasFiles(e)) hide();
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (isTyping()) return;
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) cb.current.onFiles(files);
    };
    const onPaste = (e: ClipboardEvent) => {
      if (isTyping() || modalOpen()) return;
      const cd = e.clipboardData;
      if (!cd) return;
      const files = Array.from(cd.files ?? []);
      if (files.length) {
        e.preventDefault();
        cb.current.onPasteFiles(files);
        return;
      }
      const text = cd.getData("text/plain");
      if (text) {
        e.preventDefault();
        cb.current.onPasteText(text);
      }
    };
    window.addEventListener("dragenter", onEnter, true);
    window.addEventListener("dragleave", onLeave, true);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDropCapture, true);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragend", hide, true);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragenter", onEnter, true);
      window.removeEventListener("dragleave", onLeave, true);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDropCapture, true);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragend", hide, true);
      window.removeEventListener("paste", onPaste);
      setOver(false);
    };
  }, [opts.enabled]);
  return over;
}
